import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Settings } from "@mui/icons-material";
import { updateVenueHireSettings } from "./api";
import type { Club, VenueHireSettings } from "./types";
import DialogHeader from "./DialogHeader";
import { normalizeVenueHireSettings } from "./venueHireUtils";

interface VenueHireSettingsDialogProps {
  club: Club | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const defaultSettings = (): VenueHireSettings =>
  normalizeVenueHireSettings();

const VenueHireSettingsDialog: React.FC<VenueHireSettingsDialogProps> = ({
  club,
  onClose,
  onSuccess,
  onError,
}) => {
  const [settings, setSettings] = useState<VenueHireSettings>(defaultSettings());
  const [freeBookingCode, setFreeBookingCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (club) {
      setSettings(normalizeVenueHireSettings(club.venue_hire));
      setFreeBookingCode(club.venue_hire_free_booking_code ?? "");
    }
  }, [club]);

  const handleSave = async () => {
    if (!club) return;
    try {
      setSaving(true);
      await updateVenueHireSettings(club.id, settings, freeBookingCode.trim() || null);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error saving venue hire settings:", err);
      onError("Failed to save venue hire settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={club != null}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Venue Hire Settings"
        subtitle={club?.name}
        icon={<Settings color="primary" />}
        onClose={onClose}
        closeDisabled={saving}
      />
      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={settings.enabled}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, enabled: e.target.checked }))
                }
                disabled={saving}
              />
            }
            label="Allow venue hire for DIY sessions"
          />

          <Typography variant="body2" color="text.secondary">
            Set separate prices for 30-minute, 60-minute, and 90-minute bookings.
          </Typography>

          <TextField
            label="Price for 30-minute slot"
            type="number"
            value={settings.price_per_30_min}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                price_per_30_min: Math.max(0, Number(e.target.value)),
              }))
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            disabled={saving}
            fullWidth
          />

          <TextField
            label="Price for 60-minute slot"
            type="number"
            value={settings.price_per_60_min}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                price_per_60_min: Math.max(0, Number(e.target.value)),
              }))
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            disabled={saving}
            fullWidth
          />

          <TextField
            label="Price for 90-minute slot"
            type="number"
            value={settings.price_per_90_min}
            onChange={(e) =>
              setSettings((s) => ({
                ...s,
                price_per_90_min: Math.max(0, Number(e.target.value)),
              }))
            }
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
            inputProps={{ min: 0, step: 0.5 }}
            disabled={saving}
            fullWidth
          />

          <TextField
            label="Notes for bookers"
            placeholder="e.g. Access via side gate, bring your own equipment"
            multiline
            rows={3}
            value={settings.notes ?? ""}
            onChange={(e) =>
              setSettings((s) => ({ ...s, notes: e.target.value }))
            }
            disabled={saving}
            fullWidth
          />

          <TextField
            label="Free booking voucher code"
            placeholder="e.g. CLUBGUEST"
            value={freeBookingCode}
            onChange={(e) => setFreeBookingCode(e.target.value)}
            helperText="Anyone who enters this code books for free. Leave blank to disable."
            disabled={saving}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <Settings />}
        >
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueHireSettingsDialog;
